import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { MessagesManagementService } from '../../services/messagesManagementService';
import { MessageCacheService } from '../../services/messageCache';
import {
  ExtendedWebsiteMessage,
  MessageFilters as MessageFiltersType,
  MessageStats,
  MessageStateHelpers,
  MessageAction,
  BulkAction
} from '../../types/messages';
import MessageSearch from '../../components/messages/MessageSearch';
import MessageFilters from '../../components/messages/MessageFilters';
import MessageListItem from '../../components/messages/MessageListItem';
import MessageDetailPanel from '../../components/messages/MessageDetailPanel';
import './MessagesPageEmail.css';

/**
 * Email-style MessagesPage with two-column layout
 * Left: Message list | Right: Message detail
 */
const MessagesPageEmail: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [messages, setMessages] = useState<ExtendedWebsiteMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<MessageFiltersType>({
    readStatus: 'all',
    type: 'all',
    source: 'all',
    dateRange: {}
  });
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // Get active message
  const activeMessage = useMemo(() =>
    messages.find(m => m.id === activeMessageId) || null,
    [messages, activeMessageId]
  );

  // Calculate stats
  const messageStats = useMemo(() =>
    MessageStateHelpers.calculateMessageStats(messages),
    [messages]
  );

  /**
   * Load messages
   */
  const loadMessages = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const msgs = await MessagesManagementService.getAllUserMessages(user.id);
      setMessages(msgs);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Apply filters and search
   */
  const filteredMessages = useMemo(() => {
    let result = [...messages];

    // Filter by read status
    // Note: A message is considered "read" if it has been viewed OR dismissed
    // This ensures dismissed messages (e.g., survey completed, team applications) don't appear as unread
    if (filters.readStatus === 'unread') {
      result = result.filter(m => !m.viewedAt && !m.isDismissed);
    } else if (filters.readStatus === 'read') {
      result = result.filter(m => m.viewedAt || m.isDismissed);
    }

    // Filter by type
    if (filters.type !== 'all') {
      result = result.filter(m => m.message_type === filters.type);
    }

    // Filter by source
    if (filters.source !== 'all') {
      result = result.filter(m => m.messageSource === filters.source);
    }

    // Filter by bookmarked
    if (filters.bookmarked) {
      result = result.filter(m => m.isBookmarked);
    }

    // Filter by date range
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);
      result = result.filter(m => new Date(m.created_at) >= startDate);
    }
    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      result = result.filter(m => new Date(m.created_at) <= endDate);
    }

    // Search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.message_text.toLowerCase().includes(query) ||
        m.message_type.toLowerCase().includes(query) ||
        (m.projectTitle && m.projectTitle.toLowerCase().includes(query))
      );
    }

    // Sort by date (newest first)
    result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return result;
  }, [messages, filters, searchQuery]);

  /**
   * Handle message selection
   */
  const handleMessageSelect = useCallback((messageId: string, selected: boolean) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(messageId);
      } else {
        newSet.delete(messageId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle message click - open detail view
   */
  const handleMessageClick = useCallback(async (messageId: string) => {
    setActiveMessageId(messageId);
    setShowMobileDetail(true);

    // Mark as dismissed if not already dismissed
    const message = messages.find(m => m.id === messageId);
    console.log('[MessagesPage] Message clicked:', {
      messageId,
      found: !!message,
      isDismissed: message?.isDismissed,
      messageType: message?.message_type,
      messageSource: message?.messageSource,
      willUpdate: message && !message.isDismissed && user
    });

    if (message && !message.isDismissed && user) {
      // Optimistic update - mark as dismissed
      setMessages(prev => prev.map(m =>
        m.id === messageId ? {
          ...m,
          isDismissed: true,
          dismissedAt: new Date().toISOString(),
          viewedAt: new Date().toISOString() // Also set viewedAt for consistency
        } : m
      ));

      // Update server
      try {
        console.log('[MessagesPage] Calling trackMessageInteraction...');
        // Track interaction (this will set dismissed_at for team messages)
        await MessagesManagementService.trackMessageInteraction(
          messageId,
          user.id,
          'view'
        );
        console.log('[MessagesPage] trackMessageInteraction completed');

        console.log('[MessagesPage] Calling dismissMessage...');
        // Also call dismissMessage to ensure it's dismissed
        // (This is redundant for team messages but needed for other types)
        await MessagesManagementService.dismissMessage(messageId, user.id);
        console.log('[MessagesPage] dismissMessage completed');

        MessageCacheService.invalidateUserCache(user.id);

        // Dispatch custom event to update sidebar count
        window.dispatchEvent(new CustomEvent('messageRead', {
          detail: { messageIds: [messageId] }
        }));
        console.log('[MessagesPage] All updates completed successfully');
      } catch (err) {
        console.error('[MessagesPage] Error marking message as dismissed:', err);
      }
    } else {
      console.log('[MessagesPage] Skipping update - message already dismissed or not found');
    }
  }, [messages, user]);

  /**
   * Handle message action
   */
  const handleMessageAction = useCallback(async (action: MessageAction) => {
    if (!activeMessage || !user) return;

    try {
      switch (action) {
        case 'mark_read':
          // Mark as read AND dismiss (treat read as dismissed)
          setMessages(prev => prev.map(m =>
            m.id === activeMessage.id ? {
              ...m,
              viewedAt: new Date().toISOString(),
              isDismissed: true,
              dismissedAt: new Date().toISOString()
            } : m
          ));
          await MessagesManagementService.trackMessageInteraction(activeMessage.id, user.id, 'view');
          await MessagesManagementService.dismissMessage(activeMessage.id, user.id);
          MessageCacheService.invalidateUserCache(user.id); // Invalidate cache to update sidebar
          break;

        case 'mark_unread':
          // Mark as unread AND undismiss (treat unread as undismissed)
          setMessages(prev => prev.map(m =>
            m.id === activeMessage.id ? {
              ...m,
              viewedAt: undefined,
              isDismissed: false,
              dismissedAt: undefined
            } : m
          ));
          await MessagesManagementService.undismissMessage(activeMessage.id, user.id);
          MessageCacheService.invalidateUserCache(user.id); // Invalidate cache to update sidebar
          break;

        case 'bookmark':
          setMessages(prev => prev.map(m =>
            m.id === activeMessage.id ? { ...m, isBookmarked: true } : m
          ));
          await MessagesManagementService.bookmarkMessage(activeMessage.id, user.id);
          MessageCacheService.invalidateUserCache(user.id); // Invalidate cache to update sidebar
          break;

        case 'unbookmark':
          setMessages(prev => prev.map(m =>
            m.id === activeMessage.id ? { ...m, isBookmarked: false } : m
          ));
          await MessagesManagementService.unbookmarkMessage(activeMessage.id, user.id);
          MessageCacheService.invalidateUserCache(user.id); // Invalidate cache to update sidebar
          break;

        case 'click_link':
          await MessagesManagementService.trackMessageInteraction(activeMessage.id, user.id, 'click');
          break;

        case 'action_button_click':
          await MessagesManagementService.trackActionButtonClick(activeMessage.id, user.id, 'action_button', 'Action Button');
          break;
      }

      // Final cache invalidation for any action
      MessageCacheService.invalidateUserCache(user.id);

      // Dispatch custom event to update sidebar count for read/unread actions
      if (action === 'mark_read' || action === 'mark_unread') {
        window.dispatchEvent(new CustomEvent('messageRead', {
          detail: { messageIds: [activeMessage.id] }
        }));
      }
    } catch (err) {
      console.error('Error handling message action:', err);
    }
  }, [activeMessage, user]);

  /**
   * Handle bulk actions
   */
  const handleBulkAction = useCallback(async (actionType: BulkAction['type']) => {
    if (selectedMessages.size === 0 || !user) return;

    const messageIds = Array.from(selectedMessages);

    try {
      switch (actionType) {
        case 'mark_read':
          // Mark as read AND dismiss (treat read as dismissed)
          setMessages(prev => prev.map(m =>
            messageIds.includes(m.id) ? {
              ...m,
              viewedAt: new Date().toISOString(),
              isDismissed: true,
              dismissedAt: new Date().toISOString()
            } : m
          ));
          for (const id of messageIds) {
            await MessagesManagementService.trackMessageInteraction(id, user.id, 'view');
          }
          await MessagesManagementService.bulkDismissMessages(messageIds, user.id);
          break;

        case 'mark_unread':
          // Mark as unread AND undismiss (treat unread as undismissed)
          setMessages(prev => prev.map(m =>
            messageIds.includes(m.id) ? {
              ...m,
              viewedAt: undefined,
              isDismissed: false,
              dismissedAt: undefined
            } : m
          ));
          await MessagesManagementService.bulkUndismissMessages(messageIds, user.id);
          break;

        case 'bookmark':
          setMessages(prev => prev.map(m =>
            messageIds.includes(m.id) ? { ...m, isBookmarked: true } : m
          ));
          await MessagesManagementService.bulkBookmarkMessages(messageIds, user.id);
          break;

        case 'unbookmark':
          setMessages(prev => prev.map(m =>
            messageIds.includes(m.id) ? { ...m, isBookmarked: false } : m
          ));
          await MessagesManagementService.bulkUnbookmarkMessages(messageIds, user.id);
          break;

        case 'delete':
          // Remove messages from the list (set is_active to false)
          setMessages(prev => prev.filter(m => !messageIds.includes(m.id)));
          await MessagesManagementService.bulkDeleteMessages(messageIds, user.id);
          break;
      }

      setSelectedMessages(new Set());
      MessageCacheService.invalidateUserCache(user.id);

      // Dispatch custom event to update sidebar count for read/unread actions
      if (actionType === 'mark_read' || actionType === 'mark_unread') {
        window.dispatchEvent(new CustomEvent('messageRead', {
          detail: { messageIds }
        }));
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
    }
  }, [selectedMessages, user]);

  /**
   * Handle link click
   */
  const handleLinkClick = useCallback(async (url: string, isExternal: boolean) => {
    if (activeMessage && user) {
      await MessagesManagementService.trackLinkClick(activeMessage.id, user.id, url, isExternal);
    }

    if (isExternal) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  }, [activeMessage, user]);

  /**
   * Select all/none
   */
  const handleSelectAll = useCallback(() => {
    if (selectedMessages.size === filteredMessages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(filteredMessages.map(m => m.id)));
    }
  }, [selectedMessages.size, filteredMessages]);

  // Load messages on mount
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Handle deep link from URL
  useEffect(() => {
    const messageId = searchParams.get('messageId');
    if (messageId && messages.length > 0) {
      handleMessageClick(messageId);
      setSearchParams({}); // Clear URL parameter
    }
  }, [searchParams, messages, handleMessageClick, setSearchParams]);

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="messages-page-email">
        {/* Header with search and filters */}
        <div className="messages-header">
          <MessageSearch
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            resultCount={filteredMessages.length}
          />
          <MessageFilters
            filters={filters}
            onFiltersChange={setFilters}
            messageStats={messageStats}
          />
        </div>

        {/* Toolbar */}
        <div className="messages-toolbar">
          <div className="toolbar-left">
            <input
              type="checkbox"
              checked={selectedMessages.size === filteredMessages.length && filteredMessages.length > 0}
              onChange={handleSelectAll}
              aria-label={t('messagesPage.selectAll')}
            />
            <span className="message-count">
              {selectedMessages.size > 0
                ? t('messagesPage.countSelected', { count: selectedMessages.size })
                : t('messagesPage.countTotal', { count: filteredMessages.length })}
            </span>
          </div>

          {selectedMessages.size > 0 && (
            <div className="toolbar-actions">
              <button onClick={() => handleBulkAction('mark_read')} title={t('messagesPage.markRead')}>
                📭 {t('messagesPage.markRead')}
              </button>
              <button onClick={() => handleBulkAction('mark_unread')} title={t('messagesPage.markUnread')}>
                📧 {t('messagesPage.markUnread')}
              </button>
              <button onClick={() => handleBulkAction('bookmark')} title={t('messagesPage.bookmark')}>
                ⭐ {t('messagesPage.bookmark')}
              </button>
              <button onClick={() => handleBulkAction('unbookmark')} title={t('messagesPage.unbookmark')}>
                ☆ {t('messagesPage.unbookmark')}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(t('messagesPage.deleteConfirm', { count: selectedMessages.size }))) {
                    handleBulkAction('delete');
                  }
                }}
                title={t('messagesPage.delete')}
                className="delete-btn"
              >
                🗑️ {t('messagesPage.delete')}
              </button>
            </div>
          )}
        </div>

        {/* Two-column layout */}
        <div className={`messages-content ${showMobileDetail ? 'show-detail' : ''}`}>
          {/* Left: Message List */}
          <div className="messages-list-column">
            {loading && <div className="loading-state">{t('messagesPage.loading')}</div>}
            {error && <div className="error-state">{t('messagesPage.loadError')}: {error}</div>}
            {!loading && !error && filteredMessages.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>{t('messagesPage.notFound')}</h3>
                <p>{t('messagesPage.notFoundHint')}</p>
              </div>
            )}
            {!loading && !error && filteredMessages.map(message => (
              <MessageListItem
                key={message.id}
                message={message}
                selected={selectedMessages.has(message.id)}
                isActive={message.id === activeMessageId}
                onSelect={(selected) => handleMessageSelect(message.id, selected)}
                onClick={() => handleMessageClick(message.id)}
              />
            ))}
          </div>

          {/* Right: Message Detail */}
          <div className="messages-detail-column">
            <button
              className="mobile-back-btn"
              onClick={() => setShowMobileDetail(false)}
              aria-label={t('messagesPage.backToList')}
            >
              ← {t('messagesPage.back')}
            </button>
            <MessageDetailPanel
              message={activeMessage}
              onAction={handleMessageAction}
              onLinkClick={handleLinkClick}
              onClose={() => {
                setActiveMessageId(null);
                setShowMobileDetail(false);
              }}
            />
          </div>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default MessagesPageEmail;
